// Route permission mapping using PERMISSION_ENTITIES configuration
import { PERMISSION_ENTITIES } from "../config/permissionsConfig";

export interface RoutePermissionRequirement {
  entity: string;
  action: string;
  adminOnly?: boolean; // Flag to indicate admin-only routes
}

// Admin-only routes that require isAdmin = true
const ADMIN_ONLY_ROUTES: string[] = [
  "/admin",
  "/resource-columns",
  "/file-processing-attributes",
  "/domain/import-entity",
  "/resource/import-entity",
  "/resource-column/import-entity",
  "/rules/import-entity",
];

// Route to entity mapping - maps URL prefixes to permission entities
// These must match the entity keys defined in PERMISSION_ENTITIES
const ROUTE_TO_ENTITY_MAP: { [key: string]: string } = {
  "/domain": "domain",
  "/resource": "resource",
  "/rules": "rule",
  "/rules-list": "rule",
  "/validation-execution-history": "validation_result",
  "/rules-execution-history": "rule_result",
  "/connection-keys": "connection_key",
  "/linked-services": "linked_service",
  "/blob-list": "blob_list",
  "/sftp-list": "sftp_list",
  "/system": "system",
  "/users": "user",
  "/roles": "user_role",
  "/groups": "groups",
  "/tags": "auth_tag",
  "/file-processing-attributes": "file_processing_attributes",
  "/domain-linkage": "domain_linkage",
  "/dashboard": "",
  "/research-query": "generic_research_query",
  "/research-execution-history": "generic_research_query_result",
  "/issue-list": "validation_issues",
  "/run-instance": "run_instance",
};

// Dynamic route pattern generation based on PERMISSION_ENTITIES
// This creates route patterns automatically from the entity configuration
const generateRoutePatterns = () => {
  const patterns: { pattern: RegExp; entity: string; action: string }[] = [];

  // Add specific patterns for user group assignment routes
  patterns.push({
    pattern: new RegExp(`^\\/groups\\/\\d+\\/assign-users$`),
    entity: "user_group_users",
    action: "view",
  });

  // Iterate through each entity in PERMISSION_ENTITIES
  PERMISSION_ENTITIES.forEach((entity) => {
    // Get the route prefix for this entity from ROUTE_TO_ENTITY_MAP
    const routePrefix = Object.keys(ROUTE_TO_ENTITY_MAP).find(
      (prefix) => ROUTE_TO_ENTITY_MAP[prefix] === entity.key,
    );

    if (!routePrefix) return; // Skip entities without route mapping

    // Generate patterns for each action the entity supports
    entity.actions.forEach((action) => {
      switch (action.key) {
        case "add":
          patterns.push({
            pattern: new RegExp(`^${routePrefix.replace(/\//g, "\\/")}\\/add$`),
            entity: entity.key,
            action: action.key,
          });
          break;

        case "edit":
          patterns.push({
            pattern: new RegExp(
              `^${routePrefix.replace(/\//g, "\\/")}\\/edit\\/\\d+$`,
            ),
            entity: entity.key,
            action: action.key,
          });
          // Handle nested edit routes (like rules, resources, and resource columns)
          if (
            entity.key === "rule" ||
            entity.key === "resource" ||
            entity.key === "resource_column_details"
          ) {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/[^/]+\\/edit\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }

          break;

        case "view":
          patterns.push({
            pattern: new RegExp(
              `^${routePrefix.replace(/\//g, "\\/")}\\/view\\/\\d+$`,
            ),
            entity: entity.key,
            action: action.key,
          });
          // Handle nested view routes (like rules, resources, and resource columns)
          if (
            entity.key === "rule" ||
            entity.key === "resource" ||
            entity.key === "resource_column_details"
          ) {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/[^/]+\\/view\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            // Rule execution and research query routes also require view permission
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/\\d+\\/rule-execution\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/\\d+\\/re-run-rule-execution\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/\\d+\\/researchQuery\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }
          // Handle audit routes (require view permission)
          if (entity.key === "domain") {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/auditDomain\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }
          if (entity.key === "resource") {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/validate\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/auditResource\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            // Handle nested resource routes with domainId
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/[^/]+\\/auditResource\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/[^/]+\\/validate-resource\\/\\d+$`,
              ),
              entity: entity.key,
              action: "validate", // This route specifically requires validate permission
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/[^/]+\\/re-run-validate-resource\\/\\d+$`,
              ),
              entity: entity.key,
              action: "validate", // This route specifically requires validate permission
            });
          }
          // Handle resource column specific routes
          if (entity.key === "resource_column_details") {
            // Handle nested resource column routes with domainId
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/[^/]+\\/auditResourceColumn\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }
          // Handle research query execution routes
          if (entity.key === "generic_research_query") {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/\\d+\\/execute$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/\\d+\\/re-run\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            // Handle audit route for generic research query
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(/\//g, "\\/")}\\/auditGenericResearchQuery\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }
          break;

        case "execute":
          // Handle execution routes for rules and research queries
          if (entity.key === "rule") {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/\\d+\\/rule-execution\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/\\d+\\/re-run-rule-execution\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }
          if (entity.key === "generic_research_query") {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/research-query-execution\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/re-run-research-query-execution\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }
          break;

        case "validate":
          // Handle validation routes for resources
          if (entity.key === "resource") {
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/\\d+\\/validate-resource\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
            patterns.push({
              pattern: new RegExp(
                `^${routePrefix.replace(
                  /\//g,
                  "\\/",
                )}\\/\\d+\\/re-run-validate-resource\\/\\d+$`,
              ),
              entity: entity.key,
              action: action.key,
            });
          }
          break;

        case "delete":
          // Delete actions are typically handled via API calls, not separate routes
          // But we can add patterns if needed in the future
          break;
      }
    });
  });

  return patterns;
};

export const findRouteRequirement = (
  pathname: string,
): RoutePermissionRequirement | undefined => {
  // Check if this is an admin-only route
  const isAdminOnlyRoute = ADMIN_ONLY_ROUTES.some((adminRoute) =>
    pathname.startsWith(adminRoute),
  );

  if (isAdminOnlyRoute) {
    return {
      entity: "admin", // Special entity for admin-only routes
      action: "access",
      adminOnly: true,
    };
  }

  // Generate and check specific route patterns that require specific actions
  const specificRoutePatterns = generateRoutePatterns();
  for (const routePattern of specificRoutePatterns) {
    if (routePattern.pattern.test(pathname)) {
      // Find the entity in PERMISSION_ENTITIES to validate it exists
      const entity = PERMISSION_ENTITIES.find(
        (e) => e.key === routePattern.entity,
      );
      if (entity) {
        // Check if the entity supports the required action
        const action = entity.actions.find(
          (a) => a.key === routePattern.action,
        );
        if (action) {
          return {
            entity: routePattern.entity,
            action: routePattern.action,
          };
        }
      }
    }
  }

  // Fall back to general route prefix matching for "view" permission
  const matchingPrefix = Object.keys(ROUTE_TO_ENTITY_MAP).find((prefix) =>
    pathname.startsWith(prefix),
  );

  if (!matchingPrefix) {
    return undefined;
  }

  const entityKey = ROUTE_TO_ENTITY_MAP[matchingPrefix];

  // Skip empty entity keys (like dashboard)
  if (!entityKey) {
    return undefined;
  }

  // Find the entity in PERMISSION_ENTITIES
  const entity = PERMISSION_ENTITIES.find((e) => e.key === entityKey);

  if (!entity) {
    return undefined;
  }

  // Default to "view" action for route access, but entity must support it
  const viewAction = entity.actions.find((action) => action.key === "view");

  if (!viewAction) {
    return undefined;
  }

  return {
    entity: entity.key,
    action: "view",
  };
};

// Utility function to check if a user has permission for a specific route
export const hasRoutePermission = (
  pathname: string,
  hasPermissionFn: (entity: string, action: string) => boolean,
  isAdmin: boolean = false,
): boolean => {
  // Admin users have access to all routes
  if (isAdmin) return true;

  // Find the route requirement
  const requirement = findRouteRequirement(pathname);

  // If no requirement found, allow access (public route)
  if (!requirement) return true;

  // Check admin-only routes
  if (requirement.adminOnly) {
    return isAdmin;
  }

  // Check if user has the required permission
  return hasPermissionFn(requirement.entity, requirement.action);
};

// Utility function to get the required action for a route
export const getRequiredActionForRoute = (
  pathname: string,
): string | undefined => {
  const requirement = findRouteRequirement(pathname);
  return requirement?.action;
};

// Utility function to get the required entity for a route
export const getRequiredEntityForRoute = (
  pathname: string,
): string | undefined => {
  const requirement = findRouteRequirement(pathname);
  return requirement?.entity;
};

// Function to validate if a route pattern is correctly configured
export const validateRoutePattern = (
  pathname: string,
): {
  isValid: boolean;
  entity?: string;
  action?: string;
  error?: string;
} => {
  const requirement = findRouteRequirement(pathname);

  if (!requirement) {
    return {
      isValid: true, // Public routes are valid
      error: "No permission requirement (public route)",
    };
  }

  if (requirement.adminOnly) {
    return {
      isValid: true,
      entity: requirement.entity,
      action: requirement.action,
    };
  }

  // Check if entity exists in PERMISSION_ENTITIES
  const entity = PERMISSION_ENTITIES.find((e) => e.key === requirement.entity);
  if (!entity) {
    return {
      isValid: false,
      error: `Entity '${requirement.entity}' not found in PERMISSION_ENTITIES`,
    };
  }

  // Check if action exists for the entity
  const action = entity.actions.find((a) => a.key === requirement.action);
  if (!action) {
    return {
      isValid: false,
      entity: requirement.entity,
      error: `Action '${requirement.action}' not found for entity '${requirement.entity}'`,
    };
  }

  return {
    isValid: true,
    entity: requirement.entity,
    action: requirement.action,
  };
};

// Utility function to get all generated route patterns (for debugging/testing)
export const getAllRoutePatterns = (): {
  pattern: string;
  entity: string;
  action: string;
}[] => {
  return generateRoutePatterns().map((p) => ({
    pattern: p.pattern.source,
    entity: p.entity,
    action: p.action,
  }));
};

// Utility function to check if a specific entity-action combination has route patterns
export const hasRoutePatternsForEntityAction = (
  entity: string,
  action: string,
): boolean => {
  const patterns = generateRoutePatterns();
  return patterns.some((p) => p.entity === entity && p.action === action);
};

// Utility function to get route patterns for a specific entity
export const getRoutePatternsForEntity = (
  entityKey: string,
): { pattern: string; action: string }[] => {
  return generateRoutePatterns()
    .filter((p) => p.entity === entityKey)
    .map((p) => ({
      pattern: p.pattern.source,
      action: p.action,
    }));
};
